// src/pages/locations/hooks/useCitiesList.ts
import React from "react";

import {
  useCountries,
  useRegions,
  useCities,
  useDeleteCityMutation,
} from "@/api/features/locations";
import type {
  LocationCountry,
  LocationRegion,
  LocationCity,
} from "@/api/features/locations/types";

import { toRegionId } from "../useLocationScope";
import { useDeleteConfirm } from "./useDeleteConfirm";

const PAGE_SIZE = 10;

/** The list endpoints cap `limit` at 100 — dropdowns must stay within it. */
const DROPDOWN_LIMIT = 100;

export type CitiesFilters = {
  name?: string;
};

/**
 * List + delete state for the Cities tab.
 * Country and region come from the URL (see useLocationScope).
 */
export function useCitiesList(params: {
  countryIso2: string;
  region: string;
  search: string;
}) {
  const countryIso2 = String(params.countryIso2 ?? "").trim().toUpperCase();
  const region = String(params.region ?? "").trim();
  const search = params.search;

  const [filters, setFilters] = React.useState<CitiesFilters>({});
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [countryIso2, region, search, filters.name]);

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: DROPDOWN_LIMIT,
  });

  const countries = React.useMemo<LocationCountry[]>(
    () => countriesRes?.items ?? [],
    [countriesRes]
  );

  const { data: regionsRes, isLoading: regionsLoading } = useRegions(
    countryIso2
      ? { countryIso2, page: 1, limit: DROPDOWN_LIMIT }
      : { countryIso2: "" }
  );

  const regions = React.useMemo<LocationRegion[]>(
    () => regionsRes?.items ?? [],
    [regionsRes]
  );

  const canList = countryIso2 !== "";

  const {
    data: listRes,
    isLoading,
    isFetching,
  } = useCities(
    canList
      ? {
          countryIso2,
          page,
          limit: PAGE_SIZE,
          q: search.trim() || undefined,
          regionId: toRegionId(region),
          name: filters.name?.trim() || undefined,
        }
      : { countryIso2: "" }
  );

  const rows = React.useMemo<LocationCity[]>(() => listRes?.items ?? [], [listRes]);
  const total = Number(listRes?.total ?? 0);

  const deleteMutation = useDeleteCityMutation();
  const deleting = deleteMutation.isPending;

  const remove = React.useCallback(
    (row: LocationCity, done: () => void) => {
      deleteMutation.mutate({ id: row.id }, { onSettled: done });
    },
    [deleteMutation]
  );

  const { confirmDelete, askDelete, cancelDelete, confirmDeleteRow } =
    useDeleteConfirm<LocationCity>({ isDeleting: deleting, remove });

  return {
    countries,
    countriesLoading,

    regions,
    regionsLoading,

    filters,
    setFilters,

    page,
    setPage,
    pageSize: PAGE_SIZE,

    rows,
    total,
    isLoading: canList ? isLoading : countriesLoading,
    isFetching,

    deleting,
    confirmDelete,
    askDelete,
    cancelDelete,
    confirmDeleteRow,
  };
}
